import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Alert,
  Badge,
  Button,
  EmptyState,
  HelpTip,
  PageContainer,
  PageHeader,
} from '@/components/ui';
import { api } from '@/lib/api';

import { formatDataHoraSegundos } from '@/lib/format-date';
/**
 * `Manutenção › Chamados` — o que os clientes abrem pelo AuvoDesk.
 *
 * 🔴 **Ler aqui, agir lá.** Não há um botão que altere chamado, e a tela diz
 * isso com todas as letras — senão alguém procura um "responder" que não
 * existe. O AuvoDesk é o dono.
 *
 * 🔴 **O que só existe aqui é o cruzamento com o monitoramento.** O AuvoDesk não
 * tem como saber que o cliente que abriu chamado tem equipamento fora do ar
 * agora. O IGM tem — e é isso que transforma a tela de cópia da lista de lá em
 * *o chamado e a causa provável, lado a lado.*
 */

interface Monitoramento {
  foraDoAr: number;
  desdeIso: string | null;
  latenciaMs: number | null;
}

interface Chamado {
  id: string;
  titulo: string;
  descricao: string;
  clienteId: string | null;
  clienteNome: string;
  identificado: boolean;
  estado: string;
  encerrado: boolean;
  prioridade: string | null;
  tipoDeSolicitacao: string | null;
  quemAbriu: string | null;
  criadoEmIso: string | null;
  abertoHaDias: number | null;
  anexos: string[];
  monitoramento: Monitoramento | null;
}

interface Lista {
  itens: Chamado[];
  total: number;
  abertos: number;
  semVinculo: number;
}

const AJUDA =
  'A lista vem do AuvoDesk e é somente leitura: para responder ou encerrar, use o AuvoDesk. ' +
  'O que só existe aqui é a coluna do monitoramento — se o cliente que abriu o chamado tem ' +
  'equipamento fora do ar neste momento.';

function aberto(dias: number | null): string {
  if (dias === null) return '';
  if (dias === 0) return 'aberto hoje';
  if (dias === 1) return 'aberto há 1 dia';
  return `aberto há ${dias} dias`;
}

function cor(dias: number | null): 'neutral' | 'warning' | 'danger' {
  if (dias === null) return 'neutral';
  if (dias >= 7) return 'danger';
  if (dias >= 3) return 'warning';
  return 'neutral';
}

export default function ChamadosPage() {
  const [lista, setLista] = useState<Lista | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [verEncerrados, setVerEncerrados] = useState(false);
  const [aberto1, setAberto1] = useState<string | null>(null);

  const carregar = useCallback(() => {
    setErro(null);
    setCarregando(true);
    api
      .get<Lista>(`/api/v1/auvo-chamados${verEncerrados ? '?encerrados=1' : ''}`)
      .then(setLista)
      .catch((e: Error) => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [verEncerrados]);

  useEffect(carregar, [carregar]);

  return (
    <PageContainer>
      <PageHeader
        title="Chamados"
        subtitle="O que os clientes abriram pelo AuvoDesk, do mais antigo para o mais novo."
      />

      <Alert variant="info" className="mb-4">
        <strong>Ler aqui, agir lá.</strong> Esta lista é somente leitura — para responder ou
        encerrar um chamado, use o AuvoDesk. <HelpTip texto={AJUDA} />
      </Alert>

      {carregando && <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>}
      {erro && <Alert variant="danger">{erro}</Alert>}

      {lista && (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1">
              {lista.abertos === 0
                ? 'Nenhum chamado aberto.'
                : `${lista.abertos} ${lista.abertos === 1 ? 'chamado aberto' : 'chamados abertos'}`}
            </span>
            {lista.semVinculo > 0 && (
              <Badge variant="warning" size="sm">
                {lista.semVinculo} sem cliente identificado
              </Badge>
            )}
            <Button size="sm" variant="ghost" onClick={() => setVerEncerrados((v) => !v)}>
              {verEncerrados ? 'Esconder encerrados' : 'Ver também os encerrados'}
            </Button>
          </div>

          {lista.itens.length === 0 ? (
            <EmptyState
              title={verEncerrados ? 'Nenhum chamado no período' : 'Nenhum chamado aberto'}
              description={
                'O IGM lê os chamados dos últimos 90 dias. Se estiver vazio e você esperava algo, ' +
                'confira a integração em Configurações › Integrações › Auvo.'
              }
              data-testid="chamados-vazio"
            />
          ) : (
            <ul className="space-y-3" data-testid="chamados-lista">
              {lista.itens.map((c) => (
                <li
                  key={c.id}
                  className="rounded-lg border border-line bg-surface p-4 dark:border-line-dark dark:bg-surface-dark"
                  data-testid="chamado-item"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-ink-1 dark:text-ink-dark-1">{c.titulo}</p>
                      <p className="text-sm text-ink-2 dark:text-ink-dark-2">{c.clienteNome}</p>
                      {!c.identificado && (
                        <p className="mt-1 text-xs text-warning">
                          Cliente ainda não identificado — o nome acima é o do Auvo.{' '}
                          <Link to="/admin/integracoes" className="underline">
                            Resolver em Vínculos
                          </Link>
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {/* 🔴 O estado separa o que precisa de ação do que já acabou. */}
                      <Badge variant={c.encerrado ? 'neutral' : 'success'} size="sm">
                        {c.estado}
                      </Badge>
                      {!c.encerrado && c.abertoHaDias !== null && (
                        <Badge variant={cor(c.abertoHaDias)} size="sm">
                          {aberto(c.abertoHaDias)}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* 🔴 O CRUZAMENTO — o que o AuvoDesk não sabe. */}
                  {c.monitoramento && c.monitoramento.foraDoAr > 0 && (
                    <Alert variant="danger" className="mt-2" data-testid="chamado-cruzamento">
                      Este cliente tem <strong>{c.monitoramento.foraDoAr}</strong>{' '}
                      {c.monitoramento.foraDoAr === 1 ? 'equipamento fora do ar' : 'equipamentos fora do ar'}{' '}
                      agora
                      {c.monitoramento.desdeIso &&
                        `, desde ${formatDataHoraSegundos(c.monitoramento.desdeIso)}`}
                      .
                    </Alert>
                  )}
                  {c.monitoramento && c.monitoramento.foraDoAr === 0 && (
                    <p className="mt-2 text-xs text-ink-3 dark:text-ink-dark-3">
                      Monitoramento: tudo no ar neste cliente
                      {c.monitoramento.latenciaMs !== null &&
                        ` · última resposta em ${c.monitoramento.latenciaMs} ms`}
                      .
                    </p>
                  )}
                  {/*
                    ⚠️ Sem coletor não é "tudo bem": é "não sabemos". Dizer a
                    diferença é o que impede a tela de mentir por omissão.
                  */}
                  {!c.monitoramento && (
                    <p className="mt-2 text-xs text-ink-3 dark:text-ink-dark-3">
                      Este cliente não tem monitoramento — não dá para saber se há equipamento fora
                      do ar.
                    </p>
                  )}

                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-ink-3 dark:text-ink-dark-3">
                    {c.tipoDeSolicitacao && <span>{c.tipoDeSolicitacao}</span>}
                    {c.prioridade && <span>prioridade {c.prioridade}</span>}
                    {c.quemAbriu && <span>aberto por {c.quemAbriu}</span>}
                    {c.criadoEmIso && (
                      <span>{formatDataHoraSegundos(c.criadoEmIso)}</span>
                    )}
                    <button
                      type="button"
                      className="underline"
                      onClick={() => setAberto1(aberto1 === c.id ? null : c.id)}
                      data-testid="ver-solicitacao"
                    >
                      {aberto1 === c.id ? 'esconder a solicitação' : 'ler a solicitação'}
                    </button>
                  </div>

                  {aberto1 === c.id && (
                    <div className="mt-3 rounded-lg bg-paper-warm p-3 dark:bg-paper-dark-warm">
                      <p className="whitespace-pre-wrap text-sm text-ink-1 dark:text-ink-dark-1">
                        {c.descricao || '(o chamado foi aberto sem descrição)'}
                      </p>
                      {c.anexos.length > 0 && (
                        <ul className="mt-2 space-y-1 text-xs">
                          {c.anexos.map((a) => (
                            <li key={a}>
                              <a href={a} target="_blank" rel="noreferrer" className="underline">
                                ver anexo
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </PageContainer>
  );
}
